import Header from "../components/Header";
import Footer from "../components/Footer";
import PageSeo from "../components/PageSeo";
import { SUPPORT_EMAIL, absoluteUrl } from "../seo/siteData";


function PrivacyPolicy() {
  const currentYear = new Date().getFullYear();
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacy Policy - Zarkit",
      url: absoluteUrl("/privacy-policy"),
      description:
        "Read the Zarkit privacy policy for website users, customers, and delivery partners in Jhansi.",
    },
  ];

  return (
    <>
      <PageSeo
        title="Privacy Policy"
        description="Read the Zarkit privacy policy for website visitors, customers, and delivery partners using our local delivery and goods transport platform in Jhansi."
        path="/privacy-policy"
        keywords="Zarkit privacy policy, Jhansi delivery app privacy, Zarkit data policy"
        structuredData={structuredData}
      />

      <div className="site-shell">
        <Header />

        <main>
          <div className="privacy-wrap">

            <div className="doc-title">Privacy Policy for Zarkit</div>
            <div className="meta">
              Effective Date: 02-03-2026 &nbsp;|&nbsp; Last Updated: 02-03-2026
            </div>

            <div className="intro-block">
              <p>Zarkit ("Zarkit", "we", "our", "us") is an on-demand logistics and delivery platform operated under the registered name Zarkit Group. We are committed to protecting the privacy and personal data of our users, customers, partners, drivers, and vendors.</p>
              <p>This Privacy Policy explains how we collect, use, store, share, and protect your information when you use the Zarkit mobile application, website, or related services (collectively, the "Services").</p>
              <p>By accessing or using Zarkit, you agree to the practices described in this Privacy Policy.</p>
            </div>

            {/* Section 1 */}
            <h2><span className="sec-badge">1</span> Company Information</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
                <li><strong>Owner:</strong> Ravi Kumar</li>
                <li>
                  <strong>Registered Address:</strong><br />
                  Jouri Bujurg, Near Parichha,<br />
                  Jhansi, Uttar Pradesh – 284121, India
                </li>
                <li><strong>Support Email:</strong> {SUPPORT_EMAIL}</li>
              </ul>
            </div>

            {/* Section 2 */}
            <h2><span className="sec-badge">2</span> Scope of This Privacy Policy</h2>
            <p>This Privacy Policy applies to:</p>
            <ul>
              <li>Customers requesting delivery or logistics services</li>
              <li>Drivers / delivery partners</li>
              <li>Business users and vendors</li>
              <li>Website and app visitors</li>
              <li>Customer support interactions</li>
            </ul>

            {/* Section 3 */}
            <h2><span className="sec-badge">3</span> Information We Collect</h2>

            <h3>3.1 Information You Provide Directly</h3>
            <p>We may collect the following personal information:</p>

            <h3>For Customers</h3>
            <div className="info-panel">
              <ul>
                <li>Full name</li>
                <li>Mobile number</li>
                <li>Email address</li>
                <li>Pickup and delivery addresses</li>
                <li>Payment-related details (processed securely via third parties)</li>
                <li>Order details and delivery instructions</li>
                <li>Communication records (chat, email, call)</li>
              </ul>
            </div>

            <h3>For Delivery Partners / Drivers</h3>
            <div className="info-panel">
              <ul>
                <li>Name, phone number, email</li>
                <li>Address</li>
                <li>Government-issued ID details (KYC)</li>
                <li>Vehicle details (type, registration number)</li>
                <li>Driving license details</li>
                <li>Bank account details (for payouts)</li>
              </ul>
            </div>

            <h3>For Business Accounts</h3>
            <div className="info-panel">
              <ul>
                <li>Business name</li>
                <li>GST details (if applicable)</li>
                <li>Billing address</li>
                <li>Authorized contact details</li>
              </ul>
            </div>

            <h3>3.2 Information Collected Automatically</h3>
            <div className="info-panel">
              <ul>
                <li>Device information (model, OS, app version)</li>
                <li>IP address</li>
                <li>Location data (real-time during delivery)</li>
                <li>Log files and crash data</li>
                <li>Usage patterns and app interactions</li>
                <li>Cookies and similar technologies (for website)</li>
              </ul>
            </div>

            {/* Section 4 */}
            <h2><span className="sec-badge">4</span> Location Data Usage</h2>
            <p>Zarkit collects real-time location data to:</p>
            <ul>
              <li>Assign nearby delivery partners</li>
              <li>Track pickups and deliveries</li>
              <li>Improve route efficiency</li>
              <li>Ensure safety and fraud prevention</li>
            </ul>
            <div className="warn-panel">
              <p>📍 Location access is required only during active service usage.</p>
            </div>

            {/* Section 5 */}
            <h2><span className="sec-badge">5</span> How We Use Your Information</h2>
            <ul>
              <li>Provide on-demand logistics and delivery services</li>
              <li>Match customers with delivery partners</li>
              <li>Process orders and payments</li>
              <li>Communicate service updates</li>
              <li>Provide customer support</li>
              <li>Verify identity and prevent fraud</li>
              <li>Improve platform performance</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>

            {/* Section 6 */}
            <h2><span className="sec-badge">6</span> Payments &amp; Financial Information</h2>
            <p>Zarkit does not store sensitive payment information such as:</p>
            <ul>
              <li>Card numbers</li>
              <li>CVV</li>
              <li>UPI credentials</li>
            </ul>
            <div className="warn-panel">
              <p>💳 All payments are processed securely through trusted third-party payment gateways that comply with industry security standards.</p>
            </div>

            {/* Section 7 */}
            <h2><span className="sec-badge">7</span> Data Sharing &amp; Disclosure</h2>

            <h3>7.1 With Service Providers</h3>
            <div className="info-panel">
              <ul>
                <li>Payment processors</li>
                <li>Cloud hosting providers</li>
                <li>Analytics and monitoring tools</li>
                <li>Customer support tools</li>
              </ul>
            </div>

            <h3>7.2 With Delivery Partners</h3>
            <div className="info-panel">
              <ul>
                <li>Pickup and drop location</li>
                <li>Customer contact details (only required for delivery)</li>
              </ul>
            </div>

            <h3>7.3 Legal &amp; Safety Reasons</h3>
            <div className="info-panel">
              <ul>
                <li>Government or law enforcement requests</li>
                <li>Court orders or legal obligations</li>
                <li>To protect Zarkit's legal rights, users, or public safety</li>
              </ul>
            </div>

            <div className="highlight-note">
              🔒 We do NOT sell or rent your personal data to advertisers or third parties.
            </div>

            {/* Section 8 */}
            <h2><span className="sec-badge">8</span> Data Retention Policy</h2>
            <p>We retain personal data only as long as:</p>
            <ul>
              <li>Necessary to provide services</li>
              <li>Required by law or regulatory authorities</li>
              <li>Needed for dispute resolution or fraud prevention</li>
            </ul>
            <p>After this period, data is securely deleted or anonymized.</p>

            {/* Section 9 */}
            <h2><span className="sec-badge">9</span> Data Security Measures</h2>
            <div className="info-panel">
              <ul>
                <li>Secure servers</li>
                <li>Encrypted data transmission</li>
                <li>Restricted access controls</li>
                <li>Regular security reviews</li>
              </ul>
            </div>
            <p>However, no system is completely secure, and users share data at their own risk.</p>

            {/* Section 10 */}
            <h2><span className="sec-badge">10</span> User Rights &amp; Choices</h2>
            <ul>
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and data</li>
              <li>Withdraw consent (subject to legal limits)</li>
            </ul>
            <p>Requests can be sent to: <strong>support@zarkit.com</strong></p>

            {/* Section 11 */}
            <h2><span className="sec-badge">11</span> Account Deletion</h2>
            <p>Users may request permanent account deletion by:</p>
            <ul>
              <li>Emailing support@zarkit.com</li>
              <li>Mentioning registered mobile number / email</li>
            </ul>
            <div className="warn-panel">
              <p>🕐 Account deletion may take 7–30 working days depending on verification and legal requirements.</p>
            </div>

            {/* Section 12 */}
            <h2><span className="sec-badge">12</span> Children's Privacy</h2>
            <p>Zarkit services are not intended for children under 13 years.</p>
            <p>We do not knowingly collect personal data from minors.</p>
            <p>If such data is identified, it will be deleted immediately.</p>

            {/* Section 13 */}
            <h2><span className="sec-badge">13</span> Third-Party Links &amp; Services</h2>
            <p>Zarkit may contain links to third-party websites or services.</p>
            <p>We are not responsible for their privacy practices.</p>
            <p>Users should review third-party privacy policies separately.</p>

            {/* Section 14 */}
            <h2><span className="sec-badge">14</span> Changes to This Privacy Policy</h2>
            <p>Zarkit reserves the right to update this Privacy Policy at any time.</p>
            <p>Updates will be posted with a revised "Last Updated" date.</p>
            <p>Continued use of the Services constitutes acceptance of changes.</p>

            {/* Section 15 */}
            <h2><span className="sec-badge">15</span> Governing Law</h2>
            <p>This Privacy Policy is governed by the laws of India.</p>
            <p>Any disputes shall be subject to the jurisdiction of Uttar Pradesh courts.</p>

            {/* Section 16 */}
            <h2><span className="sec-badge">16</span> Contact Information</h2>
            <div className="dark-contact">
              <p>For any questions or privacy concerns:</p>
              <br />
              <p>✉ Email: <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
              <br />
              <p>
                🏠 Registered Address:<br />
                Jouri Bujurg, Near Parichha,<br />
                Jhansi, Uttar Pradesh – 284121, India
              </p>
            </div>

          </div>
        </main>

        <Footer currentYear={currentYear} />
      </div>
    </>
  );
}

export default PrivacyPolicy;
